import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Ref } from 'react';

import { Match, PodsResponse, Pod } from '../../types';

interface OfficialScoreSheetPrintProps {
	matches: Match[];
	innerRef?: Ref<HTMLDivElement>;
	sheetSize?: 'Half Sheets' | 'Full Sheets';
	podsResponse?: PodsResponse;
	timeZoneIana?: string;
	teamOneTitle: string;
	teamTwoTitle: string;
	shouldDisplayPlayerNames?: {
		showHomeLineup: boolean;
		showAwayLineup: boolean;
	};
}

interface ScoreTableProps {
	maxScore: number;
	timeoutCount?: number;
	isFiveGameFormat?: boolean;
}

interface ScoreRowProps extends ScoreTableProps {
	isFirstRow?: boolean;
}

interface GenericScoreTableProps {
	scoreFormatMaxGames: number;
	scoreFormatGameMaxValues: number[];
	scoreFormatGameOriginalValues: number[];
	timeoutCount?: number;
	isFiveGameFormat?: boolean;
}

const getPlayers = (isDoubles: boolean, player1: string, player2?: string, shouldShowPlayers?: boolean): (string | undefined)[] => {
	if (shouldShowPlayers === false) {
		return isDoubles ? ['', ''] : [''];
	}
	return isDoubles ? [player1, player2] : [player1];
};

const getAdjustedMaxScore = (maxScore: number, shorthand: string = ''): number => {
	const isWinByTwo = shorthand.toLowerCase().includes('by 2');
	if (isWinByTwo) return 25;
	return Math.min(maxScore, 25);
};

const getScoreBoxWidth = (maxScore: number): string => {
	if (maxScore === 25) return 'flex-1 min-w-0';
	if (maxScore < 21) return 'min-w-8';
	if (maxScore >= 23) return 'min-w-[20px]';
	return 'min-w-6';
};

const getTimeoutHeaderWidth = (timeoutCount: number): string => {
	if (timeoutCount === 1) return 'w-[30px]';
	if (timeoutCount === 2) return 'w-[60px]';
	if (timeoutCount === 3) return 'w-[90px]';
	return 'w-[30px]';
};

const getBorderStyles = (isFirstRow: boolean, colIdx?: number) => ({
	borderTopWidth: isFirstRow ? '1px' : '0px',
	borderTopStyle: 'dashed' as const,
	borderBottomWidth: '1px',
	borderBottomStyle: 'dashed' as const,
	borderRightWidth: '1px',
	borderRightStyle: 'dashed' as const,
	borderLeftWidth: colIdx === 0 ? '1px' : '0px',
	borderLeftStyle: 'dashed' as const
});

const PlayerNamesWithMT = ({
	players,
	isFiveGameFormat = false,
	showVwTwTf = false
}: {
	players: (string | undefined)[];
	isFiveGameFormat?: boolean;
	showVwTwTf?: boolean;
}) => {
	return (
		<div className="flex flex-row items-end justify-between gap-6 w-full">
			{players.length === 2 ? (
				<div className={clsx('flex flex-row items-center', isFiveGameFormat ? 'gap-1' : 'gap-2')}>
					{players.map((name, index) => (
						<div key={`player-${index}`} className="flex flex-col">
							<div
								className={clsx(
									'text-center border-b border-gray-900 font-bold mb-0.5 pb-0.5 min-w-44',
									isFiveGameFormat ? 'text-[10px] min-h-3' : 'text-xs min-h-4'
								)}
							>
								{name}
							</div>
							<div className="flex items-center justify-center">
								<span className={clsx('border border-gray-900 p-0.5', isFiveGameFormat ? 'text-[8px]' : 'text-xs')}>MT</span>
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="flex flex-col items-start">
					<div
						className={clsx(
							'border-b border-gray-900 font-bold text-start mb-0.5 pb-0.5 min-w-44',
							isFiveGameFormat ? 'text-[10px] min-h-3' : 'text-xs min-h-4'
						)}
					>
						{players[0]}
					</div>
					<div className={clsx('flex items-center gap-1', isFiveGameFormat ? 'mb-0.5' : 'mb-1')}>
						<span className={isFiveGameFormat ? 'text-[8px]' : 'text-[10px]'}>MT</span>
						<div className={clsx('border border-gray-900 ml-0.5 mr-0.5', isFiveGameFormat ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
					</div>
				</div>
			)}
			{showVwTwTf ? <VwTwTfBoxes isFiveGameFormat={isFiveGameFormat} /> : null}
		</div>
	);
};

const MatchSeparator = ({ isFiveGameFormat = false }: { isFiveGameFormat?: boolean }) => (
	<div className={clsx('flex items-center justify-around', isFiveGameFormat ? 'my-1' : 'my-3')}>
		<div className={clsx('text-gray-700', isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>
			If above wins match,
			<br />
			all scores & initial
		</div>
		<div className="flex gap-2">
			{Array.from({ length: 4 }, (_, i) => (
				<div key={i} className={clsx('h-px bg-gray-900', isFiveGameFormat ? 'w-10' : 'w-12')} />
			))}
		</div>
		<div className={clsx('text-gray-700 transform rotate-180', isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>
			If above wins match,
			<br />
			all scores & initial
		</div>
	</div>
);

const ScoreTableHeader = ({ timeoutCount = 0, isFiveGameFormat = false }: ScoreTableProps) => (
	<div className={clsx('flex flex-row items-start justify-between', isFiveGameFormat ? 'mb-0.5' : 'mb-1')}>
		<div className={clsx('font-bold min-w-10 text-center', isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>SRV</div>
		<div className={clsx('flex flex-row gap-0', isFiveGameFormat ? 'ml-3' : 'ml-4')}>
			<div className={clsx('font-bold text-center', isFiveGameFormat ? 'text-[10px]' : 'text-xs', getTimeoutHeaderWidth(timeoutCount))}>
				T/O
			</div>
		</div>
	</div>
);

const TimeoutBoxes = ({
	timeoutCount,
	isFirstRow,
	isFiveGameFormat = false
}: {
	timeoutCount: number;
	isFirstRow: boolean;
	isFiveGameFormat?: boolean;
}) => (
	<div
		className="flex flex-row border-gray-400"
		style={{
			borderTopWidth: isFirstRow ? '1px' : '0px',
			borderTopStyle: 'solid'
		}}
	>
		{Array.from({ length: timeoutCount }, (_, idx) => (
			<div
				key={`timeout-${idx}`}
				className={clsx(
					'border-b border-gray-400 flex items-center justify-center',
					{
						'border-l': idx === 0,
						'border-r': true
					},
					isFiveGameFormat ? 'w-[30px] h-[22px]' : 'w-[30px] h-[28px]'
				)}
			/>
		))}
	</div>
);

const VwTwTfBoxes = ({ isFiveGameFormat = false }: { isFiveGameFormat?: boolean }) => (
	<div className="flex flex-col items-center self-end">
		<div className={clsx('font-bold text-center mb-0.5', isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>VW / TW / TF</div>
		<div className="flex flex-row">
			{Array.from({ length: 4 }, (_, idx) => (
				<div
					key={`vw-tw-tf-${idx}`}
					className={clsx(
						'border-t border-b border-gray-400 flex items-center justify-center',
						{
							'border-l border-r': idx === 0,
							'border-r': idx > 0
						},
						isFiveGameFormat ? 'w-[30px] h-[22px]' : 'w-[30px] h-[28px]'
					)}
				/>
			))}
		</div>
	</div>
);

const ScoreRow = ({ maxScore, isFirstRow = false, timeoutCount = 0, isFiveGameFormat = false }: ScoreRowProps) => (
	<div className="flex flex-row w-full items-stretch">
		<div className={clsx('border-b border-gray-400 flex items-center justify-center min-w-10', isFiveGameFormat ? 'min-h-5' : 'min-h-7')} />

		<div
			className={clsx('flex flex-row items-center', {
				'flex-1': maxScore === 25
			})}
		>
			{Array.from({ length: maxScore + 1 }, (_, colIdx) => (
				<div
					key={colIdx}
					className={clsx(
						'border-gray-400 flex items-center justify-center',
						getScoreBoxWidth(maxScore),
						isFiveGameFormat ? 'min-h-5' : 'min-h-7'
					)}
					style={getBorderStyles(isFirstRow, colIdx)}
				>
					<span className={clsx('font-medium', isFiveGameFormat ? 'text-xs' : 'text-sm')}>{colIdx}</span>
				</div>
			))}
		</div>

		<div className={clsx('flex flex-row pl-2 ml-auto self-end')}>
			{timeoutCount > 0 ? (
				<TimeoutBoxes timeoutCount={timeoutCount} isFirstRow={isFirstRow} isFiveGameFormat={isFiveGameFormat} />
			) : isFirstRow ? (
				<div className="w-7 border-b border-gray-400" />
			) : (
				<div className="w-7" />
			)}
		</div>
	</div>
);

const getTimeoutWidthInPx = (timeoutCount: number): number => {
	if (timeoutCount === 1) return 30;
	if (timeoutCount === 2) return 60;
	if (timeoutCount === 3) return 90;
	return 30;
};

const SwitchEndsTriangle = ({
	displayMaxScore,
	targetMaxScore,
	timeoutCount = 0
}: {
	displayMaxScore: number;
	targetMaxScore: number;
	timeoutCount?: number;
}) => {
	const switchEndsPosition = Math.ceil(targetMaxScore / 2);
	const totalRightSpace = getTimeoutWidthInPx(timeoutCount) + 8;

	return (
		<div className="flex flex-row items-center">
			<div className="min-w-10" />
			<div
				className={clsx('flex flex-row items-center', {
					'flex-1': displayMaxScore === 25
				})}
			>
				{Array.from({ length: displayMaxScore + 1 }, (_, colIdx) => {
					const isSwitchEndsPosition = colIdx === switchEndsPosition;
					return (
						<div key={`triangle-${colIdx}`} className={clsx('h-4 flex items-center justify-center', getScoreBoxWidth(displayMaxScore))}>
							{!!isSwitchEndsPosition && (
								<svg width="17" height="13" viewBox="0 0 17 13" className="fill-none">
									<polygon points="9,2 16,12 2,12" stroke="black" strokeWidth="1" fill="none" />
								</svg>
							)}
						</div>
					);
				})}
			</div>

			<div style={{ width: `${totalRightSpace}px` }} className="ml-auto" />
		</div>
	);
};

const GenericScoreTable = ({
	scoreFormatMaxGames,
	scoreFormatGameMaxValues,
	scoreFormatGameOriginalValues,
	timeoutCount = 0,
	isFiveGameFormat = false
}: GenericScoreTableProps) => {
	const displayHeaderMaxScore = scoreFormatGameMaxValues[0] ?? 11;
	const targetHeaderMaxScore = scoreFormatGameOriginalValues[0] ?? 11;

	return (
		<div className={clsx('flex flex-col w-full', isFiveGameFormat ? 'mt-1.5 mb-2' : 'my-4')}>
			<ScoreTableHeader maxScore={displayHeaderMaxScore} timeoutCount={timeoutCount} isFiveGameFormat={isFiveGameFormat} />
			{Array.from({ length: scoreFormatMaxGames }, (_, rowIdx) => {
				const isFirstRow = rowIdx === 0;
				const gameMaxScore = scoreFormatGameMaxValues[rowIdx] ?? displayHeaderMaxScore;
				return (
					<ScoreRow
						key={rowIdx}
						maxScore={gameMaxScore}
						isFirstRow={isFirstRow}
						timeoutCount={timeoutCount}
						isFiveGameFormat={isFiveGameFormat}
					/>
				);
			})}
			<SwitchEndsTriangle displayMaxScore={displayHeaderMaxScore} targetMaxScore={targetHeaderMaxScore} timeoutCount={timeoutCount} />
		</div>
	);
};

const MatchHeader = ({
	match,
	teamOnePlayers,
	podsResponse,
	timeZoneIana,
	teamTwoTitle,
	isFiveGameFormat = false
}: {
	match: Match;
	teamOnePlayers: (string | undefined)[];
	podsResponse?: PodsResponse;
	timeZoneIana?: string;
	teamTwoTitle: string;
	isFiveGameFormat?: boolean;
}) => {
	dayjs.extend(utc);
	dayjs.extend(timezone);

	let podTitle: string | undefined;
	let podNumber: string | undefined;
	let formattedPodTitle: string = '';

	if (podsResponse && podsResponse.pods) {
		podTitle = podsResponse.pods.find((pod: Pod) => pod.uuid === match.teamLeagueSeasonDivisionPodUuid)?.name;
		podNumber = podTitle?.split(' ').pop();
		formattedPodTitle = podNumber ? `P${podNumber}` : '';
	}
	const matchStartDateTime = dayjs.utc(match.matchPlannedStart).tz(timeZoneIana).format('MMM D, YYYY | hh:mm A');

	return (
		<div className="flex justify-between items-start">
			<div className={clsx('flex flex-col justify-between', isFiveGameFormat ? 'h-16' : 'h-20')}>
				<h2 className={clsx('font-bold border-b border-gray-900 min-w-[310px]', isFiveGameFormat ? 'text-sm mb-0.5' : 'text-base mb-0.5')}>
					{formattedPodTitle} {match.roundText}
				</h2>
				<div className="flex flex-col">
					<p
						className={clsx(
							'font-semibold text-gray-700 border-b border-gray-900 min-w-[310px]',
							isFiveGameFormat ? 'text-xs mb-0.5' : 'text-sm mb-1'
						)}
					>
						{teamTwoTitle} (A)
					</p>
					<PlayerNamesWithMT players={teamOnePlayers} isFiveGameFormat={isFiveGameFormat} />
				</div>
			</div>
			<VwTwTfBoxes isFiveGameFormat={isFiveGameFormat} />
			<div className={clsx('flex flex-col items-end', isFiveGameFormat ? 'gap-1' : 'gap-2')}>
				<div className="flex flex-row gap-1">
					<p className={clsx(isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>Time:</p>
					<span className={clsx('border-b border-gray-900 font-bold min-w-44', isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>
						{matchStartDateTime}
					</span>
				</div>
				<div className="flex flex-row gap-1">
					<p className={clsx(isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>Court:</p>
					<span className={clsx('border-b border-gray-900 font-bold min-w-44', isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>
						{match.courtTitle || ''}
					</span>
				</div>
				<div className="flex flex-row gap-1">
					<p className={clsx(isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>Referee:</p>
					<span className={clsx('border-b border-gray-900 font-bold min-w-44', isFiveGameFormat ? 'text-[10px]' : 'text-xs')} />
				</div>
				<div className="flex flex-row gap-1">
					<p className={clsx(isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>Format:</p>
					<span className={clsx('border-b border-gray-900 font-bold min-w-44', isFiveGameFormat ? 'text-[10px]' : 'text-xs')}>
						{match.scoreFormatShorthand}
					</span>
				</div>
			</div>
		</div>
	);
};

const MatchSheet = ({
	match,
	idx,
	isHalfSheet,
	podsResponse,
	timeZoneIana,
	teamOneTitle,
	teamTwoTitle,
	shouldDisplayPlayerNames
}: {
	match: Match;
	idx: number;
	isHalfSheet: boolean;
	podsResponse?: PodsResponse;
	timeZoneIana?: string;
	teamOneTitle: string;
	teamTwoTitle: string;
	shouldDisplayPlayerNames?: {
		showHomeLineup: boolean;
		showAwayLineup: boolean;
	};
}) => {
	const shorthand = match.scoreFormatShorthand || '';

	const originalMaxValues = [
		match.scoreFormatGame1Max,
		match.scoreFormatGame2Max,
		match.scoreFormatGame3Max,
		match.scoreFormatGame4Max,
		match.scoreFormatGame5Max
	].filter((v): v is number => typeof v === 'number');

	const adjustedMaxValues = originalMaxValues.map((v) => getAdjustedMaxScore(v, shorthand));

	const isDoubles = match.formatTitle.toLowerCase().includes('doubles');
	const teamOnePlayers = getPlayers(
		isDoubles,
		match.teamTwoPlayerOneName ?? '',
		match.teamTwoPlayerTwoName ?? '',
		shouldDisplayPlayerNames?.showAwayLineup
	);
	const teamTwoPlayers = getPlayers(
		isDoubles,
		match.teamOnePlayerOneName ?? '',
		match.teamOnePlayerTwoName ?? '',
		shouldDisplayPlayerNames?.showHomeLineup
	);

	const timeoutCount = match.timeoutCountTeamOneGameOne || 0;
	const isFiveGameFormat = (match.scoreFormatMaxGames ?? 1) === 5;

	return (
		<div
			key={match.matchUuid || idx}
			className={clsx({
				'print-half-sheet-official': isHalfSheet,
				'print-full-sheet-official': !isHalfSheet
			})}
		>
			<div>
				<MatchHeader
					match={match}
					teamOnePlayers={teamOnePlayers}
					podsResponse={podsResponse}
					timeZoneIana={timeZoneIana}
					teamTwoTitle={teamTwoTitle}
					isFiveGameFormat={isFiveGameFormat}
				/>

				<GenericScoreTable
					scoreFormatMaxGames={match.scoreFormatMaxGames ?? 1}
					scoreFormatGameMaxValues={adjustedMaxValues}
					scoreFormatGameOriginalValues={originalMaxValues}
					timeoutCount={timeoutCount}
					isFiveGameFormat={isFiveGameFormat}
				/>

				<MatchSeparator isFiveGameFormat={isFiveGameFormat} />

				<div className={clsx('rotate-180', isFiveGameFormat ? 'mt-1.5' : 'mt-4')}>
					<div className={clsx('flex flex-row justify-start items-start gap-8', isFiveGameFormat ? 'mb-0.5 mt-1.5' : 'mb-0.5 mt-2')}>
						<div className="flex flex-col items-start min-w-[120px] w-full">
							<p
								className={clsx(
									'font-semibold text-gray-700 border-b border-gray-900 min-w-[310px]',
									isFiveGameFormat ? 'text-xs mb-0.5' : 'text-sm mb-1'
								)}
							>
								{teamOneTitle} (H)
							</p>
							<PlayerNamesWithMT players={teamTwoPlayers} isFiveGameFormat={isFiveGameFormat} showVwTwTf />
						</div>
					</div>
					<GenericScoreTable
						scoreFormatMaxGames={match.scoreFormatMaxGames ?? 1}
						scoreFormatGameMaxValues={adjustedMaxValues}
						scoreFormatGameOriginalValues={originalMaxValues}
						timeoutCount={timeoutCount}
						isFiveGameFormat={isFiveGameFormat}
					/>
				</div>
			</div>
		</div>
	);
};

export const OfficialScoreSheetPrint = ({
	matches,
	innerRef,
	sheetSize = 'Half Sheets',
	podsResponse,
	timeZoneIana,
	teamOneTitle,
	teamTwoTitle,
	shouldDisplayPlayerNames
}: OfficialScoreSheetPrintProps) => {
	const isHalfSheet = sheetSize === 'Half Sheets';

	return (
		<div ref={innerRef} className="mx-0 p-0 print:block hidden print-container">
			{/* NOTE: temporary sorting matches by court title, we need better sorting logic. see ticket https://pickleball-product-team.atlassian.net/browse/PW-3969 */}
			{[...matches]
				.sort((a, b) => {
					const getCourtNumber = (title: string | null | undefined) => {
						const num = title ? parseInt(title, 10) : NaN;
						return isNaN(num) ? null : num;
					};

					const numA = getCourtNumber(a.courtTitle);
					const numB = getCourtNumber(b.courtTitle);

					// Numbers first, then alphabetical
					if (numA !== null && numB !== null) return numA - numB;
					if (numA !== null) return -1;
					if (numB !== null) return 1;
					return (a.courtTitle || '').localeCompare(b.courtTitle || '');
				})
				.map((match, idx) => (
					<MatchSheet
						key={match.matchUuid || idx}
						match={match}
						idx={idx}
						isHalfSheet={isHalfSheet}
						podsResponse={podsResponse}
						timeZoneIana={timeZoneIana}
						teamOneTitle={teamOneTitle}
						teamTwoTitle={teamTwoTitle}
						shouldDisplayPlayerNames={shouldDisplayPlayerNames}
					/>
				))}
		</div>
	);
};
